/**
 * DataGridPremium-backed replacement for Table.jsx (Material React Table v3).
 *
 * Preserves the old wrapper's PUBLIC PROP API so consumers migrate by import
 * swap. The MRT->DataGrid translation (column mapping, Cell-signature shim,
 * pagination/filter model translation, row ids) lives in tableColumnAdapter.js
 * and tableStatePersistence.js so it is unit-testable in isolation.
 *
 * In-built features (parity with the old wrapper): global "contains" search,
 * sorting, client + server pagination, sessionStorage state, row numbers,
 * collapsible detail panels, row striping/hover/selection, teal uppercase
 * sticky headers, i18n empty state.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
  DataGridPremium,
  Toolbar,
  useGridApiRef
} from '@mui/x-data-grid-premium';

import { ThemeContext } from 'src/store/ContextStore';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import DetailPanel from './DetailPanel';
import {
  buildGridColumns,
  injectRowIds,
  makeGetRowId,
  ROW_NUMBER_FIELD
} from './tableColumnAdapter';
import {
  getPreferenceDefaults,
  getTableStateFor,
  saveTableState,
  setPreferredPageSize,
  globalFilterToQuickFilterValues,
  quickFilterValuesToGlobalFilter,
  resolveInitialPagination,
  isSpuriousPageReset
} from './tableStatePersistence';

const DEFAULT_PAGE_SIZE_OPTIONS = [3, 5, 8, 10, 15, 20];

// Flatten `fg` at `weight` opacity over an opaque `bg` into a solid rgb() —
// used for the header/pinned backgrounds, which MUST be opaque so pinned
// columns fully hide the columns scrolling underneath (a semi-transparent
// tint would let them bleed through). Returns null if colours aren't 6-digit
// hex, so the caller can fall back to the translucent tint.
function opaqueBlend(fg, bg, weight) {
  const parse = (c) => {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(c || '');
    return m
      ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
      : null;
  };
  const f = parse(fg);
  const b = parse(bg);
  if (!f || !b) return null;
  const mix = f.map((c, i) => Math.round(c * weight + b[i] * (1 - weight)));
  return `rgb(${mix[0]}, ${mix[1]}, ${mix[2]})`;
}

const Container = styled('div')({
  position: 'relative',
  width: '100%',
  // Flat card: rounded corners + a thin border, NO shadow (removed per design).
  borderRadius: 12,
  border: '0.5px solid var(--color-border)',
  // Clip the (square-cornered) grid to the rounded card; the grid supplies its
  // own themed background, so the container stays transparent (no white
  // corners in dark mode).
  overflow: 'hidden'
});

// Module-scope toolbar so its identity is stable across renders — an inline
// component would remount every render and steal focus from the search box.
function TableToolbar(props) {
  const {
    t,
    title,
    subtitle,
    addClicked,
    addText,
    addBtnSize,
    buttonStyle,
    disableAddBtn,
    additionalActions,
    disableSearch,
    searchValue,
    onSearchChange,
    searchInputRef,
    searchPlaceholder
  } = props;

  return (
    <Toolbar
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 1,
        py: '16px',
        minHeight: 68
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {title ? (
          <Box sx={{ paddingX: 3, marginRight: '20px' }}>
            <Typography
              variant="h6"
              component="div"
              sx={{ fontWeight: 'bold', fontSize: '20px', lineHeight: 1.2 }}
            >
              {t(title)}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                sx={{ color: '#6b7a8d', display: 'block' }}
              >
                {t(subtitle)}
              </Typography>
            )}
          </Box>
        ) : null}

        {addClicked && (
          <Button
            style={buttonStyle}
            sx={{ ml: '15px' }}
            disabled={disableAddBtn}
            variant="contained"
            color="secondary"
            onClick={() => addClicked()}
            endIcon={<AddCircleIcon fontSize="small" />}
            size={addBtnSize ?? undefined}
          >
            {t(addText)}
          </Button>
        )}
        {additionalActions && (
          <Box sx={{ ml: addClicked ? 2 : 0 }}>{additionalActions}</Box>
        )}
      </Box>

      {!disableSearch && (
        <Box sx={{ pr: '20px', display: 'flex', alignItems: 'center' }}>
          <TextField
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            inputRef={searchInputRef}
            placeholder={searchPlaceholder}
            size="small"
            variant="standard"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label={t('Clear search')}
                      onClick={() => onSearchChange('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }
            }}
          />
        </Box>
      )}
    </Toolbar>
  );
}

function makeNoRowsOverlay(label) {
  function NoRowsOverlay() {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 200,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        {label}
      </Box>
    );
  }
  NoRowsOverlay.displayName = 'NoRowsOverlay';
  return NoRowsOverlay;
}

export default function Table(props) {
  const { theme } = useContext(ThemeContext);
  const t = useAutoTranslation();
  const internalApiRef = useGridApiRef();
  const apiRef = props.tableRef || props.tableInstanceRef || internalApiRef;

  const tableKey = props.tableKey;

  const isManualPagination = typeof props.onPaginationChange === 'function';

  // --- rows + ids -------------------------------------------------------
  const rows = useMemo(
    () => injectRowIds(props.query || props.rows || []),
    [props.query, props.rows]
  );
  const getRowId = useMemo(
    () => makeGetRowId(props.getRowId),
    [props.getRowId]
  );
  const dataRef = useRef(rows);
  dataRef.current = rows;

  // --- persisted / initial state ---------------------------------------
  const savedState = useMemo(() => getTableStateFor(tableKey), [tableKey]);

  const [pagination, setPagination] = useState(() =>
    resolveInitialPagination({
      savedState,
      defaults: getPreferenceDefaults(),
      propPage: props.page,
      propPageSize: props.pageSize,
      isManualPagination
    })
  );
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  const isFilterControlled = typeof props.onGlobalFilterChange === 'function';
  const [internalGlobalFilter, setInternalGlobalFilter] = useState(
    () => savedState?.quickFilter ?? ''
  );
  const globalFilter = isFilterControlled
    ? props.globalFilter ?? ''
    : internalGlobalFilter;
  const handleGlobalFilterChange = isFilterControlled
    ? props.onGlobalFilterChange
    : setInternalGlobalFilter;

  // --- sync page/pageSize from props (controlled server consumers) ------
  const prevPageRef = useRef(props.page);
  const prevPageSizeRef = useRef(props.pageSize);
  useEffect(() => {
    if (props.page !== undefined && props.page !== prevPageRef.current) {
      prevPageRef.current = props.page;
      setPagination((prev) => ({ ...prev, page: props.page }));
    }
  }, [props.page]);
  useEffect(() => {
    if (
      props.pageSize !== undefined &&
      props.pageSize !== prevPageSizeRef.current
    ) {
      prevPageSizeRef.current = props.pageSize;
      setPagination((prev) => ({ ...prev, pageSize: props.pageSize }));
    }
  }, [props.pageSize]);

  // --- persist to sessionStorage (skip while data empty) ----------------
  useEffect(() => {
    if (dataRef.current.length === 0) return;
    saveTableState(tableKey, {
      page: pagination.page,
      pageSize: pagination.pageSize,
      quickFilter: globalFilter
    });
  }, [tableKey, pagination.page, pagination.pageSize, globalFilter]);

  // --- pagination change ------------------------------------------------
  const handlePaginationModelChange = useCallback(
    (next) => {
      if (isManualPagination) {
        // Server mode — hand the DataGrid-native model straight to the parent
        // (decision #2) and reflect it locally so the control stays in sync.
        //
        // ...except for the grid's own out-of-range correction. This guard used
        // to exist only on the client path below, so a server-paginated table
        // whose rowCount dipped to 0 mid-request passed the grid's "page 0"
        // straight to the parent and threw the user back to the first page.
        if (
          isSpuriousPageReset({
            next,
            prev: paginationRef.current,
            rowsLength: dataRef.current.length,
            totalCount: props.totalCount
          })
        ) {
          return;
        }
        setPagination(next);
        props.onPaginationChange(next);
        return;
      }
      setPagination((prev) => {
        // Ignore the spurious reset-to-page-0 the grid emits when data is
        // momentarily empty (parity with the old wrapper).
        if (dataRef.current.length === 0 && next.page === 0 && prev.page > 0) {
          return prev;
        }
        if (next.pageSize !== prev.pageSize)
          setPreferredPageSize(next.pageSize);
        return next;
      });
    },
    [isManualPagination, props]
  );

  // --- columns ----------------------------------------------------------
  const gridColumns = useMemo(
    () => buildGridColumns(props.columns, {}),
    [props.columns]
  );

  const rowNumberColumn = useMemo(
    () => ({
      field: ROW_NUMBER_FIELD,
      headerName: '',
      width: 56,
      minWidth: 48,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableExport: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const rel =
          params.api?.getRowIndexRelativeToVisibleRows?.(params.id) ?? 0;
        const { page, pageSize } = paginationRef.current || {
          page: 0,
          pageSize: 0
        };
        return rel + 1 + page * pageSize;
      }
    }),
    []
  );

  const columns = useMemo(
    () =>
      props.hideRowNumbers ? gridColumns : [rowNumberColumn, ...gridColumns],
    [rowNumberColumn, gridColumns, props.hideRowNumbers]
  );

  const pinnedColumns = useMemo(() => {
    const left = [
      ...(props.hideRowNumbers ? [] : [ROW_NUMBER_FIELD]),
      ...(props.columnPinning?.left || [])
    ];
    const right = [...(props.columnPinning?.right || [])];
    // Always float the actions column to the right on EVERY table (even if
    // the consumer didn't pin it), so the action icons stay at the right
    // edge and fully visible regardless of column widths.
    const hasActions = gridColumns.some((c) => c.field === 'actions');
    if (hasActions && !right.includes('actions')) right.push('actions');
    return { left, right };
  }, [props.columnPinning, gridColumns, props.hideRowNumbers]);

  // Auto-size the pinned actions column to its content (+ the "Actions" header)
  // so every icon is always visible, on any table, without a hand-tuned width.
  useEffect(() => {
    const api = apiRef.current;
    if (!api?.autosizeColumns || !rows.length) return undefined;
    if (!gridColumns.some((c) => c.field === 'actions')) return undefined;
    let cancelled = false;
    const id = setTimeout(() => {
      if (cancelled) return;
      try {
        api.autosizeColumns({ columns: ['actions'], includeHeaders: true });
      } catch {
        // autosize is best-effort; the colDef width remains as a fallback
      }
    }, 60);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [rows, gridColumns, apiRef]);

  // --- filter model (controlled quick filter) ---------------------------
  const filterModel = useMemo(
    () => ({
      items: [],
      quickFilterValues: globalFilterToQuickFilterValues(globalFilter)
    }),
    [globalFilter]
  );
  const handleFilterModelChange = useCallback(
    (model) => {
      const next = quickFilterValuesToGlobalFilter(model.quickFilterValues);
      if (next !== globalFilter) handleGlobalFilterChange(next);
    },
    [globalFilter, handleGlobalFilterChange]
  );

  // --- detail panels ----------------------------------------------------
  const getDetailPanelContent = useMemo(() => {
    if (!props.isCollapsible) return undefined;
    // Render callback (receives GridRowParams), not a component definition.
    // eslint-disable-next-line react/display-name
    return ({ row }) => (
      <DetailPanel
        rowData={row}
        boxKeys={props.boxKeys}
        columns={props.boxColumns}
        detailKey={props.detailKey}
        lazyFunction={props.detailLazyFunction}
        onRowClick={props.onRowClick}
      />
    );
  }, [
    props.isCollapsible,
    props.boxKeys,
    props.boxColumns,
    props.detailKey,
    props.detailLazyFunction,
    props.onRowClick
  ]);

  // --- row click --------------------------------------------------------
  const handleRowClick = useCallback(
    (params) => {
      if (typeof props.onRowClick === 'function') {
        props.onRowClick(params.row, params.id);
      } else if (props.isCollapsible) {
        apiRef.current?.toggleDetailPanel?.(params.id);
      }
    },
    [props, apiRef]
  );

  // --- selection highlight (supports id- OR index-based, decision #5) ---
  const getRowClassName = useCallback(
    (params) => {
      const even = params.indexRelativeToCurrentPage % 2 === 0;
      let cls = even ? 'tgc-row--even' : 'tgc-row--odd';
      if (props.highlightOnSelect && props.selectedTableDataId != null) {
        const sel = props.selectedTableDataId;
        const absIndex =
          paginationRef.current.page * paginationRef.current.pageSize +
          params.indexRelativeToCurrentPage;
        if (
          params.id === sel ||
          absIndex === sel ||
          params.indexRelativeToCurrentPage === sel
        ) {
          cls += ' tgc-row--selected';
        }
      }
      return cls;
    },
    [props.highlightOnSelect, props.selectedTableDataId]
  );

  // --- theming ----------------------------------------------------------
  const isLight = theme.appearance?.value === 'light';
  const rowColor = {
    midnight: ['#37384a', '#323645c7'],
    dark: ['#2d2d2f', '#343436']
  };
  const evenBg = isLight
    ? '#FFFFFF'
    : rowColor[theme.appearance?.value]?.[0] || '#2d2d2f';
  const oddBg = isLight
    ? '#F8F9FA'
    : rowColor[theme.appearance?.value]?.[1] || '#343436';

  // OPAQUE header tint (accent at 0.12 flattened over the surface). Must be
  // opaque so the pinned row-number/actions headers fully hide the columns
  // scrolling underneath — a translucent tint let them bleed through.
  const accentColor = theme.accent || '#2ba9bf';
  const headerBg =
    opaqueBlend(accentColor, evenBg, 0.12) || alpha(accentColor, 0.12);

  // Selected-row highlight as a LOW-opacity tint of the highlight colour (like
  // the header's ~0.12 accent tint), so the flash on click is subtle not bold.
  const rawHighlight =
    props.highlightColor || theme.appearance?.shade || '#f7e406';
  let selectedBg;
  try {
    selectedBg = alpha(rawHighlight, 0.18);
  } catch {
    selectedBg = rawHighlight;
  }

  const gridSx = useMemo(
    () => ({
      border: 'none',
      color: theme.appearance?.text,
      backgroundColor: theme.appearance?.color,
      '& .MuiDataGrid-columnHeader': {
        backgroundColor: headerBg,
        borderBottom: '0.5px solid var(--color-border)',
        borderRadius: 0
      },
      // Pinned columns (row-number left, actions right) get an OPAQUE header
      // tint + opaque cell background and sit above the scrolling columns, so
      // whatever scrolls underneath is fully hidden — no bleed-through.
      '& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight':
        {
          backgroundColor: headerBg,
          zIndex: 4
        },
      '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
        backgroundColor: evenBg
      },
      '& .MuiDataGrid-columnHeaderTitle': {
        fontWeight: 700,
        fontSize: '13px',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        color: 'var(--color-navy)'
      },
      // Vertically centre cell content (chips, pills, text) — DataGrid does
      // not centre by default, which left chips top-aligned and status pills
      // looking over-padded top/bottom.
      // Exactly ONE hairline separator per row: kill DataGrid's own cell/row
      // borders (var → transparent, cell → none) and draw a single very light
      // line on the row. This removes the doubled/too-thick lines.
      '--DataGrid-rowBorderColor': 'transparent',
      // Opaque backdrop for the pinned columns (row-number left, actions right)
      // so their cells AND headers stay solid on top while the middle columns
      // scroll underneath — the teal header tint is semi-transparent, so without
      // this the scrolling headers bled through the pinned ones.
      '--DataGrid-pinnedBackground': evenBg,
      // No focus ring around a cell/header when clicked.
      '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
        outline: 'none'
      },
      '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
        {
          outline: 'none'
        },
      '& .MuiDataGrid-cell': {
        display: 'flex',
        alignItems: 'center',
        borderBottom: 'none',
        fontSize: '1rem'
      },
      '& .tgc-row--even': { backgroundColor: evenBg },
      '& .tgc-row--odd': { backgroundColor: oddBg },
      '& .tgc-row--selected': {
        backgroundColor: `${selectedBg} !important`
      },
      '& .MuiDataGrid-row': {
        borderBottom: '1px solid rgba(0,0,0,0.045)',
        cursor: props.onRowClick ? 'pointer' : 'default'
      },
      // Hover = a very-low-opacity tint of the accent colour (the header row
      // uses the accent at 0.12; hover is lighter still), instead of grey.
      '& .MuiDataGrid-row:hover': {
        backgroundColor: `${alpha(theme.accent || '#2ba9bf', 0.07)} !important`
      },
      '& .MuiDataGrid-footerContainer': {
        backgroundColor: theme.appearance?.color,
        color: theme.appearance?.text
      }
    }),
    [theme, evenBg, oddBg, selectedBg, headerBg, isLight, props.onRowClick]
  );

  const loading =
    props.loading ??
    props.state?.isLoading ??
    props.state?.showProgressBars ??
    false;

  const NoRowsOverlay = useMemo(
    () => makeNoRowsOverlay(t('No Records to Display')),
    [t]
  );

  const toolbarSlotProps = useMemo(
    () => ({
      toolbar: {
        t,
        title: props.title,
        subtitle: props.subtitle,
        addClicked: props.addClicked,
        addText: props.addText,
        addBtnSize: props.addBtnSize,
        buttonStyle: props.buttonStyle,
        disableAddBtn: props.disableAddBtn,
        additionalActions: props.additionalActions,
        disableSearch: props.disableSearch,
        searchValue: globalFilter,
        onSearchChange: handleGlobalFilterChange,
        searchInputRef: props.searchInputRef,
        searchPlaceholder: t('Search')
      },
      // First/last page arrows on the pagination footer. NB: in MUI X v9 the
      // footer's TablePagination is the `basePagination` slot (not `pagination`),
      // so its props go under `slotProps.basePagination`.
      basePagination: {
        showFirstButton: true,
        showLastButton: true
      }
    }),
    [
      t,
      props.title,
      props.subtitle,
      props.addClicked,
      props.addText,
      props.addBtnSize,
      props.buttonStyle,
      props.disableAddBtn,
      props.additionalActions,
      props.disableSearch,
      props.searchInputRef,
      globalFilter,
      handleGlobalFilterChange
    ]
  );

  return (
    <Container>
      <DataGridPremium
        apiRef={apiRef}
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        loading={loading}
        // FIXED density + rowHeight for EVERY table so all tables read the same
        // height. NB: MUI X multiplies rowHeight by the density factor
        // (compact 0.7, comfortable 1.3) — mixing `dense` per table is exactly
        // what made rows inconsistent (52→36 vs 52→68). Standard = ×1 = 52.
        density="standard"
        rowHeight={props.rowHeight ?? 52}
        columnHeaderHeight={52}
        // Grow with content (bounded by page size), matching the old MRT
        // wrapper — the grid has no fixed-height parent.
        autoHeight
        disableColumnFilter
        disableColumnSelector
        disableRowSelectionOnClick
        // Column reordering stays disabled (opt-in only, e.g. Data Explorer);
        // resizing is allowed.
        disableColumnReorder
        pagination
        paginationMode={isManualPagination ? 'server' : 'client'}
        rowCount={isManualPagination ? props.totalCount ?? 0 : undefined}
        paginationModel={pagination}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={props.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS}
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        pinnedColumns={pinnedColumns}
        onRowClick={handleRowClick}
        getRowClassName={getRowClassName}
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={props.isCollapsible ? () => 'auto' : undefined}
        // Native cell editing (opt-in via editable columns + processRowUpdate).
        editMode={props.editMode}
        isCellEditable={props.isCellEditable}
        processRowUpdate={props.processRowUpdate}
        onProcessRowUpdateError={props.onProcessRowUpdateError}
        showToolbar={!props.hideToolbar}
        slots={{ toolbar: TableToolbar, noRowsOverlay: NoRowsOverlay }}
        slotProps={toolbarSlotProps}
        localeText={{ noRowsLabel: t('No Records to Display') }}
        sx={gridSx}
      />
    </Container>
  );
}
